import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  CheckCircle2,
  Copy,
  ExternalLink,
  Key,
  LogIn,
  Mail,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserPlus
} from 'lucide-react';
import { readJsonErrorResponse } from '../../utils/portalHelpers';
import { showToast } from '../../utils/toast';
import { DEFAULT_API_TOKEN } from '../../constants/portals';

const SESSION_STORAGE_KEY = 'monitor_fiscal_account_session';

const ACCOUNT_FLOW_STEPS = [
  'Crie sua conta própria',
  'Confirme o e-mail',
  'Gere e gerencie API Keys'
];

export default function AccountPage({ getApiUrl }) {
  const [mode, setMode] = useState('login');
  const [sessionToken, setSessionToken] = useState(() => localStorage.getItem(SESSION_STORAGE_KEY) || '');
  const [user, setUser] = useState(null);
  const [keys, setKeys] = useState([]);
  const [issuedToken, setIssuedToken] = useState('');
  const [debugVerificationToken, setDebugVerificationToken] = useState('');
  const [debugPasswordResetToken, setDebugPasswordResetToken] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [isLoadingAccount, setIsLoadingAccount] = useState(false);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    keyName: '',
    keyNotes: '',
    resetToken: '',
    resetPassword: '',
    verificationToken: ''
  });

  const isAuthenticated = Boolean(sessionToken && user);
  const applicationHeaders = useMemo(() => (
    DEFAULT_API_TOKEN ? { 'x-app-token': DEFAULT_API_TOKEN } : {}
  ), []);

  const accountHeaders = useMemo(() => ({
    Accept: 'application/json',
    ...applicationHeaders,
    ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {})
  }), [applicationHeaders, sessionToken]);

  const updateForm = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const persistSession = (token) => {
    setSessionToken(token || '');
    if (token) {
      localStorage.setItem(SESSION_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  };

  const loadKeys = useCallback(async (tokenOverride = '') => {
    const token = tokenOverride || sessionToken;
    if (!token) return;

    setIsLoadingKeys(true);
    try {
      const response = await fetch(getApiUrl('account-api-keys'), {
        headers: {
          Accept: 'application/json',
          ...applicationHeaders,
          Authorization: `Bearer ${token}`
        }
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao carregar API Keys.');
      }

      setUser(payload.user || null);
      setKeys(payload.keys || []);
    } catch (error) {
      showToast(`Falha ao carregar API Keys: ${error.message}`, { type: 'error' });
    } finally {
      setIsLoadingKeys(false);
    }
  }, [applicationHeaders, getApiUrl, sessionToken]);

  const loadAccount = useCallback(async (tokenOverride = '') => {
    const token = tokenOverride || sessionToken;
    if (!token) return;

    setIsLoadingAccount(true);
    try {
      const response = await fetch(getApiUrl('account'), {
        headers: {
          Accept: 'application/json',
          ...applicationHeaders,
          Authorization: `Bearer ${token}`
        }
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        persistSession('');
        setUser(null);
        return;
      }

      setUser(payload.user || null);
      await loadKeys(token);
    } finally {
      setIsLoadingAccount(false);
    }
  }, [applicationHeaders, getApiUrl, loadKeys, sessionToken]);

  const verifyEmail = useCallback(async (token) => {
    const verificationToken = String(token || '').trim();
    if (!verificationToken) {
      showToast('Informe o token de verificação.', { type: 'warning' });
      return;
    }

    setIsBusy(true);
    try {
      const response = await fetch(getApiUrl('account'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          ...applicationHeaders,
          'Content-Type': 'application/json',
          ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {})
        },
        body: JSON.stringify({
          action: 'verify-email',
          token: verificationToken
        })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao confirmar e-mail.');
      }

      setUser(payload.user || user);
      updateForm('verificationToken', '');
      showToast('E-mail confirmado. Sua API Key já pode ser gerada.', { type: 'success' });
      await loadKeys();
    } catch (error) {
      showToast(`Falha na verificação: ${error.message}`, { type: 'error' });
    } finally {
      setIsBusy(false);
    }
  }, [applicationHeaders, getApiUrl, loadKeys, sessionToken, user]);

  useEffect(() => {
    if (sessionToken) {
      loadAccount(sessionToken);
    }
  }, [loadAccount, sessionToken]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const verificationToken = url.searchParams.get('verifyEmail');
    const passwordResetToken = url.searchParams.get('resetPassword');

    if (passwordResetToken) {
      persistSession('');
      setUser(null);
      setKeys([]);
      setIssuedToken('');
      setDebugVerificationToken('');
      setDebugPasswordResetToken('');
      setMode('reset');
      updateForm('resetToken', passwordResetToken);
      url.searchParams.delete('resetPassword');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      return;
    }

    if (!verificationToken) return;

    verifyEmail(verificationToken);
    url.searchParams.delete('verifyEmail');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, [verifyEmail]);

  const submitAuth = async () => {
    const isRegister = mode === 'register';

    if (isRegister && !form.name.trim()) {
      showToast('Informe seu nome.', { type: 'warning' });
      return;
    }

    if (!form.email.trim() || !form.password.trim()) {
      showToast('Informe e-mail e senha.', { type: 'warning' });
      return;
    }

    setIsBusy(true);
    setIssuedToken('');
    setDebugVerificationToken('');
    setDebugPasswordResetToken('');

    try {
      const response = await fetch(getApiUrl('account'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          ...applicationHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: isRegister ? 'register' : 'login',
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password
        })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha na autenticação.');
      }

      persistSession(payload.sessionToken || '');
      setUser(payload.user || null);
      setDebugVerificationToken(payload.debugVerificationToken || '');
      showToast(payload.message || 'Autenticado com sucesso.', { type: payload.user?.isEmailVerified ? 'success' : 'warning' });
      await loadKeys(payload.sessionToken || '');
    } catch (error) {
      showToast(`Falha: ${error.message}`, { type: 'error' });
    } finally {
      setIsBusy(false);
    }
  };

  const resendVerification = async () => {
    setIsBusy(true);
    setDebugVerificationToken('');

    try {
      const response = await fetch(getApiUrl('account'), {
        method: 'POST',
        headers: {
          ...accountHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'resend-verification' })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao reenviar verificação.');
      }

      setUser(payload.user || user);
      setDebugVerificationToken(payload.debugVerificationToken || '');
      showToast(payload.message || 'E-mail enviado.', { type: payload.alreadyVerified ? 'success' : 'warning' });
    } catch (error) {
      showToast(`Falha ao reenviar: ${error.message}`, { type: 'error' });
    } finally {
      setIsBusy(false);
    }
  };

  const requestPasswordReset = async () => {
    if (!form.email.trim()) {
      showToast('Informe seu e-mail para recuperar a senha.', { type: 'warning' });
      return;
    }

    setIsBusy(true);
    setDebugPasswordResetToken('');

    try {
      const response = await fetch(getApiUrl('account'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          ...applicationHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'request-password-reset',
          email: form.email.trim()
        })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao solicitar recuperação.');
      }

      setDebugPasswordResetToken(payload.debugPasswordResetToken || '');
      showToast(payload.message || 'Se o e-mail existir, enviaremos as instruções.', { type: 'success' });
    } catch (error) {
      showToast(`Falha ao recuperar senha: ${error.message}`, { type: 'error' });
    } finally {
      setIsBusy(false);
    }
  };

  const submitPasswordReset = async () => {
    if (!form.resetToken.trim() || !form.resetPassword.trim()) {
      showToast('Informe o token de recuperação e a nova senha.', { type: 'warning' });
      return;
    }

    setIsBusy(true);
    setIssuedToken('');

    try {
      const response = await fetch(getApiUrl('account'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          ...applicationHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'reset-password',
          token: form.resetToken.trim(),
          password: form.resetPassword
        })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao redefinir senha.');
      }

      persistSession(payload.sessionToken || '');
      setUser(payload.user || null);
      updateForm('resetToken', '');
      updateForm('resetPassword', '');
      setMode('login');
      showToast(payload.message || 'Senha atualizada com sucesso.', { type: 'success' });
      await loadKeys(payload.sessionToken || '');
    } catch (error) {
      showToast(`Falha ao redefinir senha: ${error.message}`, { type: 'error' });
    } finally {
      setIsBusy(false);
    }
  };

  const createApiKey = async () => {
    if (!user?.isEmailVerified) {
      showToast('Confirme seu e-mail antes de gerar uma API Key.', { type: 'warning' });
      return;
    }

    setIsBusy(true);
    setIssuedToken('');

    try {
      const response = await fetch(getApiUrl('account-api-keys'), {
        method: 'POST',
        headers: {
          ...accountHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: form.keyName.trim(),
          notes: form.keyNotes.trim()
        })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao gerar API Key.');
      }

      setIssuedToken(payload.token || '');
      setKeys((previous) => payload.key ? [payload.key, ...previous] : previous);
      updateForm('keyName', '');
      updateForm('keyNotes', '');
      showToast('API Key gerada com sucesso.', { type: 'success' });
    } catch (error) {
      showToast(`Falha ao gerar API Key: ${error.message}`, { type: 'error' });
    } finally {
      setIsBusy(false);
    }
  };

  const revokeApiKey = async (key) => {
    setIsBusy(true);
    try {
      const response = await fetch(getApiUrl('account-api-keys'), {
        method: 'DELETE',
        headers: {
          ...accountHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: key.id })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao revogar API Key.');
      }

      setKeys((previous) => previous.map((item) => item.id === key.id ? payload.key : item));
      showToast('API Key revogada.', { type: 'success' });
    } catch (error) {
      showToast(`Falha ao revogar: ${error.message}`, { type: 'error' });
    } finally {
      setIsBusy(false);
    }
  };

  const logout = async () => {
    try {
      await fetch(getApiUrl('account'), {
        method: 'POST',
        headers: {
          ...accountHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'logout' })
      });
    } catch {
      // Logout local segue mesmo se a sessão já tiver expirado no servidor.
    }

    persistSession('');
    setUser(null);
    setKeys([]);
    setIssuedToken('');
    setMode('login');
  };

  const copyText = async (value, message) => {
    await navigator.clipboard.writeText(value);
    showToast(message, { type: 'success' });
  };

  const renderBrandPanel = () => (
    <aside className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-col items-center text-center">
        <div className="relative flex h-44 w-44 items-center justify-center">
          <div className="absolute h-36 w-36 rounded-full bg-teal-500/10 blur-2xl dark:bg-teal-300/10" />
          <img
            src="/icon-master.png"
            alt="Ícone do Monitor Fiscal"
            className="relative h-32 w-32 select-none rounded-3xl object-contain drop-shadow-xl"
            draggable={false}
          />
        </div>

        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Conta da API</p>
        <h2 className="mt-2 text-lg font-black text-gray-900 dark:text-white">
          {isAuthenticated ? 'Integração pronta para operar.' : 'Seu acesso à API começa aqui.'}
        </h2>
        <p className="mt-2 text-sm leading-6 text-gray-500 dark:text-gray-400">
          {isAuthenticated
            ? 'Gerencie suas chaves, revogue acessos antigos e mantenha suas integrações fiscais sob controle.'
            : 'Cadastre-se, confirme o e-mail e gere sua API Key para integrar seus fluxos fiscais.'}
        </p>
      </div>

      <div className="mt-5 grid gap-2">
        {ACCOUNT_FLOW_STEPS.map((step, index) => (
          <div key={step} className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-[10px] font-black text-white dark:bg-white dark:text-gray-950">
              {index + 1}
            </div>
            <p className="text-xs font-bold leading-5 text-gray-700 dark:text-gray-200">{step}</p>
          </div>
        ))}
      </div>

    </aside>
  );

  const renderAuth = () => {
    if (mode === 'forgot') {
      return (
        <div className="p-4 sm:p-6 space-y-5">
          <div>
            <p className="text-sm font-black uppercase tracking-tight text-gray-900 dark:text-white">Recuperar senha</p>
            <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
              Informe o e-mail da conta para receber o link de redefinição.
            </p>
          </div>

          <input
            value={form.email}
            onChange={(event) => updateForm('email', event.target.value)}
            placeholder="E-mail"
            type="email"
            className="w-full h-11 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 text-sm font-medium focus:ring-2 focus:ring-teal-500 outline-none transition-all"
          />

          <button
            onClick={requestPasswordReset}
            disabled={isBusy}
            className="w-full h-11 rounded-xl bg-teal-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50 shadow-lg shadow-teal-500/20 active:scale-95 transition-all"
          >
            {isBusy ? 'Enviando...' : 'Enviar recuperação'}
          </button>

          {debugPasswordResetToken ? (
            <button
              onClick={() => copyText(debugPasswordResetToken, 'Token de recuperação copiado.')}
              className="w-full text-left rounded-xl bg-amber-50 dark:bg-amber-900/10 p-3 text-[11px] font-mono text-amber-800 dark:text-amber-100 break-all"
            >
              Dev token: {debugPasswordResetToken}
            </button>
          ) : null}

          <button
            type="button"
            onClick={() => setMode('login')}
            className="w-full h-10 rounded-xl border border-gray-200 dark:border-gray-700 text-[10px] font-black uppercase tracking-widest text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
          >
            Voltar para login
          </button>
        </div>
      );
    }

    if (mode === 'reset') {
      return (
        <div className="p-4 sm:p-6 space-y-5">
          <div>
            <p className="text-sm font-black uppercase tracking-tight text-gray-900 dark:text-white">Definir nova senha</p>
            <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
              Crie uma nova senha para voltar a acessar sua conta da API.
            </p>
          </div>

          <input
            value={form.resetToken}
            onChange={(event) => updateForm('resetToken', event.target.value)}
            placeholder="Token de recuperação"
            className="w-full h-11 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 text-sm font-mono focus:ring-2 focus:ring-teal-500 outline-none transition-all"
          />
          <input
            value={form.resetPassword}
            onChange={(event) => updateForm('resetPassword', event.target.value)}
            placeholder="Nova senha"
            type="password"
            className="w-full h-11 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 text-sm font-medium focus:ring-2 focus:ring-teal-500 outline-none transition-all"
          />

          <button
            onClick={submitPasswordReset}
            disabled={isBusy}
            className="w-full h-11 rounded-xl bg-teal-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50 shadow-lg shadow-teal-500/20 active:scale-95 transition-all"
          >
            {isBusy ? 'Salvando...' : 'Salvar nova senha'}
          </button>

          <button
            type="button"
            onClick={() => setMode('login')}
            className="w-full h-10 rounded-xl border border-gray-200 dark:border-gray-700 text-[10px] font-black uppercase tracking-widest text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
          >
            Voltar para login
          </button>
        </div>
      );
    }

    return (
      <div className="p-4 sm:p-6 space-y-5">
        <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-50 dark:bg-gray-900 p-1">
          {[
            { id: 'login', label: 'Entrar', icon: LogIn },
            { id: 'register', label: 'Criar conta', icon: UserPlus }
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setMode(id)}
              className={`h-10 rounded-lg flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                mode === id
                  ? 'bg-white dark:bg-gray-800 text-teal-700 dark:text-teal-300 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {mode === 'register' ? (
          <input
            value={form.name}
            onChange={(event) => updateForm('name', event.target.value)}
            placeholder="Nome"
            className="w-full h-11 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 text-sm font-medium focus:ring-2 focus:ring-teal-500 outline-none transition-all"
          />
        ) : null}

        <input
          value={form.email}
          onChange={(event) => updateForm('email', event.target.value)}
          placeholder="E-mail"
          type="email"
          className="w-full h-11 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 text-sm font-medium focus:ring-2 focus:ring-teal-500 outline-none transition-all"
        />
        <input
          value={form.password}
          onChange={(event) => updateForm('password', event.target.value)}
          placeholder="Senha"
          type="password"
          className="w-full h-11 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 text-sm font-medium focus:ring-2 focus:ring-teal-500 outline-none transition-all"
        />

        {mode === 'login' ? (
          <button
            type="button"
            onClick={() => setMode('forgot')}
            className="text-xs font-bold text-teal-600 hover:text-teal-700 dark:text-teal-300"
          >
            Esqueci minha senha
          </button>
        ) : null}

        <button
          onClick={submitAuth}
          disabled={isBusy}
          className="w-full h-11 rounded-xl bg-teal-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50 shadow-lg shadow-teal-500/20 active:scale-95 transition-all"
        >
          {isBusy ? 'Processando...' : mode === 'register' ? 'Criar conta' : 'Entrar'}
        </button>
      </div>
    );
  };

  const renderPanel = () => (
    <div className="flex min-h-[34rem] flex-col overflow-hidden">
      <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-black text-gray-900 dark:text-white truncate">{user?.name}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</div>
          </div>
          <button
            onClick={logout}
            className="h-9 px-3 rounded-xl border border-gray-200 dark:border-gray-700 text-[10px] font-black uppercase tracking-widest text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
          >
            Sair
          </button>
        </div>

        {!user?.isEmailVerified ? (
          <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/10 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <Mail size={16} className="mt-0.5 text-amber-600 dark:text-amber-300" />
              <p className="text-xs leading-5 text-amber-800 dark:text-amber-200">
                Confirme seu e-mail para liberar a geração da API Key.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={form.verificationToken}
                onChange={(event) => updateForm('verificationToken', event.target.value)}
                placeholder="Token de verificação"
                className="flex-1 h-10 bg-white dark:bg-gray-950 border border-amber-200 dark:border-amber-900/40 rounded-xl px-3 text-xs font-mono outline-none"
              />
              <button
                onClick={() => verifyEmail(form.verificationToken)}
                disabled={isBusy}
                className="h-10 px-4 rounded-xl bg-amber-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
              >
                Confirmar
              </button>
              <button
                onClick={resendVerification}
                disabled={isBusy}
                className="h-10 px-4 rounded-xl border border-amber-200 dark:border-amber-900/40 text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-200 disabled:opacity-50"
              >
                Reenviar
              </button>
            </div>
            {debugVerificationToken ? (
              <button
                onClick={() => copyText(debugVerificationToken, 'Token de verificação copiado.')}
                className="w-full text-left rounded-xl bg-white/70 dark:bg-black/20 p-3 text-[11px] font-mono text-amber-800 dark:text-amber-100 break-all"
              >
                Dev token: {debugVerificationToken}
              </button>
            ) : null}
          </div>
        ) : (
          <div className="rounded-2xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/10 p-3 flex items-center gap-2 text-xs font-bold text-emerald-800 dark:text-emerald-200">
            <CheckCircle2 size={16} />
            E-mail confirmado. Você pode gerar e gerenciar suas API Keys.
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar bg-white dark:bg-gray-950 p-4 sm:p-6 space-y-6">
        <div className="space-y-3">
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Gerar API Key</p>
          <input
            value={form.keyName}
            onChange={(event) => updateForm('keyName', event.target.value)}
            placeholder="Nome da chave"
            disabled={!user?.isEmailVerified}
            className="w-full h-11 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 text-sm font-medium focus:ring-2 focus:ring-teal-500 outline-none transition-all disabled:opacity-50"
          />
          <textarea
            value={form.keyNotes}
            onChange={(event) => updateForm('keyNotes', event.target.value)}
            placeholder="Finalidade da integração"
            disabled={!user?.isEmailVerified}
            className="w-full h-20 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-teal-500 outline-none transition-all resize-none disabled:opacity-50"
          />
          <button
            onClick={createApiKey}
            disabled={isBusy || !user?.isEmailVerified}
            className="w-full h-11 rounded-xl bg-teal-600 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-50 shadow-lg shadow-teal-500/20 active:scale-95 transition-all"
          >
            {isBusy ? 'Gerando...' : 'Gerar API Key'}
          </button>

          {issuedToken ? (
            <div className="rounded-2xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/10 p-4 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300">Nova API Key</span>
                <button
                  onClick={() => copyText(issuedToken, 'API Key copiada.')}
                  className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300 hover:underline"
                >
                  <Copy size={12} />
                  Copiar
                </button>
              </div>
              <code className="block text-[11px] break-all text-emerald-700 dark:text-emerald-200 font-mono bg-white/50 dark:bg-black/20 p-2 rounded-lg">{issuedToken}</code>
            </div>
          ) : null}
        </div>

        <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Minhas API Keys</p>
            <button
              onClick={() => loadKeys()}
              disabled={isLoadingKeys}
              className="text-[10px] font-black uppercase tracking-widest text-teal-600 hover:text-teal-700 disabled:opacity-50 flex items-center gap-1"
            >
              <RefreshCw size={12} className={isLoadingKeys ? 'animate-spin' : ''} />
              Atualizar
            </button>
          </div>

          {keys.length > 0 ? (
            <div className="grid gap-2">
              {keys.map((key) => (
                <div key={key.id} className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900/50 p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">{key.name}</div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 font-mono truncate">{key.tokenPrefix}...</div>
                      <div className="mt-2 text-[10px] text-gray-400 dark:text-gray-500">
                        {key.isActive ? 'Ativa' : 'Revogada'} · último uso: {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString('pt-BR') : 'nunca'}
                      </div>
                    </div>
                    {key.isActive ? (
                      <button
                        onClick={() => revokeApiKey(key)}
                        disabled={isBusy}
                        title="Revogar API Key"
                        className="h-9 w-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all disabled:opacity-50"
                      >
                        <Trash2 size={16} />
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center border-2 border-dashed border-gray-100 dark:border-gray-800 rounded-3xl">
              <Key size={24} className="mx-auto text-gray-300 mb-2" />
              <p className="text-xs text-gray-400 font-medium">Nenhuma API Key gerada ainda.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const content = isAuthenticated ? renderPanel() : (
    isLoadingAccount ? (
      <div className="p-10 text-center text-sm text-gray-500 dark:text-gray-400">Carregando conta...</div>
    ) : renderAuth()
  );

  return (
    <div className="min-h-screen bg-gray-100 p-3 text-gray-900 dark:bg-gray-900 dark:text-gray-100 sm:p-4 lg:p-8">
      <div className="mx-auto flex min-h-[calc(100vh-1.5rem)] w-full max-w-6xl flex-col gap-4 sm:min-h-[calc(100vh-2rem)]">
        <header className="rounded-3xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <a href="/" className="flex min-w-0 items-center gap-3">
              <img src="/icon-master.png" alt="" aria-hidden="true" className="h-11 w-11 shrink-0 rounded-2xl object-contain" />
              <div className="min-w-0">
                <p className="truncate text-sm font-black uppercase tracking-tight text-gray-900 dark:text-white">Monitor Fiscal</p>
                <p className="truncate text-[11px] font-bold text-gray-500 dark:text-gray-400">Conta da API e chaves de integração</p>
              </div>
            </a>

            <div className="flex flex-wrap gap-2">
              <a
                href="/api-docs"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 text-[10px] font-black uppercase tracking-widest text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <BookOpen className="h-4 w-4" />
                Docs da API
              </a>
              <a
                href="/"
                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 text-[10px] font-black uppercase tracking-widest text-white transition-colors hover:bg-teal-700"
              >
                <ExternalLink className="h-4 w-4" />
                Voltar ao monitor
              </a>
            </div>
          </div>
        </header>

        <main className="grid flex-1 gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <section className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="border-b border-gray-100 bg-white p-5 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-300">
                  <ShieldCheck size={20} />
                </div>
                <div className="min-w-0">
                  <h1 className="text-base font-black uppercase tracking-tight text-gray-800 dark:text-white">Conta da API</h1>
                  <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                    Crie sua conta, confirme o e-mail e gerencie suas API Keys.
                  </p>
                </div>
              </div>
            </div>

            {content}
          </section>

          <div className="lg:sticky lg:top-8 lg:self-start">
            {renderBrandPanel()}
          </div>
        </main>
      </div>
    </div>
  );
}
